import React, { useRef, useState } from 'react';
import * as THREE from 'three';
import { roomWalker } from '../state/roomBody';
import { useFrame, useThree } from '@react-three/fiber';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useHeadphones, putOnHeadphones, takeOffHeadphones } from '../state/headphones';
import { roomSpace } from '../state/interiorFloor';

const roomP = new THREE.Vector3();

// TableHeadphones — a pair of headphones on every table at The Jahva House
// (2026-10-31). Khari: "Place a pair of headphones on the table (not blocking
// the coffee). If the user selects them, they see a list of recordings that
// play just for them."
//
// Where: each table's own top, on the side away from the cups and the aisle.
// The cups sit on the table's centre line toward each chair (rooms.js `cups`);
// the sugar, candle or book sits toward the aisle (+x, scripts/make_jahva.mjs);
// so the pair lies on the window side (−x), 9cm in from the centre — on the
// 35cm top, clear of both. Room space, like the cups.
//
// Tap them within reach of the table (standing at it or sitting at it) and they
// are on you: gone from the table, the player open (HeadphonesDock). Walk away
// from the table and they come off and go back on it — the cord is only so long.

const TABLE_TOP = 0.355;      // ROOMS.jahva cups' y
const INSET = 0.091;          // 20cm from the table's centre, toward the windows
const REACH = 1.6;            // world units from the table to put them on
const CORD = 2.2;             // …and how far you can wander with them on
const INK = '#151311';
const PAD = '#2b2622';
const BRASS = '#b8925a';

function Pair({ at, rotY, onTap }) {
  const [hot, setHot] = useState(false);
  return (
    <group position={at} rotation={[0, rotY, 0]}>
      <group
        onClick={(e) => { e.stopPropagation(); onTap(); }}
        onPointerOver={(e) => { e.stopPropagation(); setHot(true); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { setHot(false); document.body.style.cursor = ''; }}
      >
        {/* Lying on the table: the two cups face down, the band arching over between them. */}
        {[-1, 1].map((s) => (
          <group key={s} position={[s * 0.034, 0.008, 0]}>
            <mesh rotation={[0, 0, Math.PI / 2]} castShadow>
              <cylinderGeometry args={[0.024, 0.024, 0.016, 20]} />
              <meshStandardMaterial color={INK} roughness={0.5} metalness={0.25} emissive={hot ? '#ffc27a' : '#000'} emissiveIntensity={hot ? 0.25 : 0} />
            </mesh>
            <mesh position={[-s * 0.009, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.02, 0.02, 0.004, 20]} />
              <meshStandardMaterial color={PAD} roughness={0.95} metalness={0} />
            </mesh>
            <mesh position={[s * 0.0085, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.012, 0.012, 0.0015, 16]} />
              <meshStandardMaterial color={BRASS} roughness={0.35} metalness={0.85} />
            </mesh>
          </group>
        ))}
        <mesh position={[0, 0.008, 0]} rotation={[0, 0, 0]}>
          <torusGeometry args={[0.034, 0.0035, 8, 24, Math.PI]} />
          <meshStandardMaterial color={INK} roughness={0.45} metalness={0.4} />
        </mesh>
        {/* A bigger, invisible thing to tap than a 7cm pair. */}
        <mesh position={[0, 0.02, 0]} visible={false}>
          <boxGeometry args={[0.13, 0.06, 0.09]} />
          <meshBasicMaterial />
        </mesh>
      </group>
    </group>
  );
}

export default function TableHeadphones({ roomId }) {
  const room = ROOMS[roomId];
  const camera = useThree((s) => s.camera);
  const on = useHeadphones((s) => (s.on?.zone === roomId ? s.on : null));
  const acc = useRef(0);
  const tables = (room?.tables || []).filter((t) => t.cups?.length);

  const centre = (t) => {
    const n = t.cups.length;
    return [t.cups.reduce((a, c) => a + c[0], 0) / n, t.cups.reduce((a, c) => a + c[2], 0) / n];
  };
  // In ROOM space: a sited café stands at its building, turned with it.
  const near = (t, r) => {
    const [cx, cz] = centre(t);
    const p = roomSpace.object ? roomSpace.object.worldToLocal(roomP.copy(roomWalker(camera))) : roomWalker(camera);
    return Math.hypot(p.x - cx, p.z - cz) < r && p.y < 1.2;
  };

  // The cord: wander off and they come off (and go back on the table).
  useFrame((_, dt) => {
    if (!on) return;
    acc.current += dt;
    if (acc.current < 0.3) return;
    acc.current = 0;
    const t = tables.find((x) => x.id === on.table);
    if (!t || !near(t, CORD)) {
      takeOffHeadphones();
      useWorldStore.getState().setSeatNote?.('Headphones back on the table');
    }
  });

  if (!tables.length) return null;
  return (
    <>
      {tables.map((t) => {
        if (on?.table === t.id) return null;   // they are on you
        const [cx, cz] = centre(t);
        return (
          <Pair
            key={t.id}
            at={[cx - INSET, TABLE_TOP, cz]}
            rotY={0.55}
            onTap={() => {
              if (!near(t, REACH)) { useWorldStore.getState().setSeatNote?.('Walk over to the table first'); return; }
              putOnHeadphones({ zone: roomId, table: t.id });
            }}
          />
        );
      })}
    </>
  );
}
